import { mtFetch } from '@/lib/mtClient'

const API_BASE = (import.meta as any).env?.VITE_API_URL || 'https://api.linguaproz.com'

export interface SuggestedTerm {
  term_source: string
  term_target: string
  definition: string
  domain: string
  approved: boolean
}

const LANG_NAMES: Record<string, string> = {
  'en':    'English',
  'pt-PT': 'European Portuguese',
  'pt-pt': 'European Portuguese',
  'pt-br': 'Brazilian Portuguese',
  'pt':    'Portuguese',
  'es':    'Spanish',
  'fr':    'French',
  'de':    'German',
  'it':    'Italian',
}

function langName(code: string): string {
  return LANG_NAMES[code] || code
}

export async function extractTerminology(
  segments: { source: string }[],
  sourceLang: string,
  targetLang: string
): Promise<SuggestedTerm[]> {
  let wordCount = 0
  const chunks: string[] = []
  for (const seg of segments) {
    const words = seg.source.split(/\s+/).filter(Boolean).length
    chunks.push(seg.source)
    wordCount += words
    if (wordCount >= 3000) break
  }
  const sourceText = chunks.join('\n').trim()
  if (!sourceText) throw new Error('No source text available for extraction')

  const srcName = langName(sourceLang)
  const tgtName = langName(targetLang)

  const system = [
    'You are a professional terminology extractor for translation projects.',
    'Identify key terms, technical vocabulary, proper nouns, and domain-specific phrases',
    'that are important for consistent translation.',
    'You MUST respond with ONLY a raw JSON array — no markdown fences, no preamble, no explanation.',
    'Each object must have exactly these four string fields: term_source, term_target, definition, domain.',
    'Extract between 8 and 20 terms. Prioritise: technical terms, product/brand names,',
    'proper nouns, domain vocabulary, repeated key phrases, and terms where a wrong',
    'translation would cause serious errors.',
  ].join(' ')

  const userMessage = `Extract key terminology from this ${srcName} text for translation into ${tgtName}.

Source text:
${sourceText}

Respond with ONLY a JSON array. Example:
[
  {
    "term_source": "example term",
    "term_target": "suggested ${tgtName} translation",
    "definition": "brief definition or usage note",
    "domain": "legal | technical | medical | financial | general"
  }
]`

  const response = await mtFetch(`${API_BASE}/api/translate/ai`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      messages: [{ role: 'user', content: userMessage }],
      system,
      max_tokens: 2048,
      tier: 'standard',
    }),
  })

  if (!response.ok) {
    const err = await response.text().catch(() => 'unknown error')
    throw new Error(`AI proxy error ${response.status}: ${err}`)
  }

  const data = await response.json()
  const rawText: string = data.content?.[0]?.text || ''

  const clean = rawText
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim()

  let parsed: any[]
  try {
    parsed = JSON.parse(clean)
  } catch {
    const match = clean.match(/\[[\s\S]*\]/)
    if (!match) throw new Error('AI returned an unexpected format — could not parse terms')
    parsed = JSON.parse(match[0])
  }

  if (!Array.isArray(parsed)) throw new Error('AI returned an unexpected format')

  return parsed
    .filter((t: any) => typeof t.term_source === 'string' && typeof t.term_target === 'string')
    .map((t: any) => ({
      term_source: String(t.term_source).trim(),
      term_target: String(t.term_target).trim(),
      definition:  String(t.definition  || '').trim(),
      domain:      String(t.domain      || 'general').trim(),
      approved:    true,
    }))
}
